import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination
} from "@mui/material";
import dayjs from "dayjs";
import { getCustomerReports } from "../services/reportService";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customer-tabpanel-${index}`}
      aria-labelledby={`customer-tab-${index}`}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const CustomerReports: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [days, setDays] = useState<number>(30);
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);

  const reportType = tabValue === 0 ? "frequent" : "inactive";

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getCustomerReports({
        type: reportType,
        days,
        page,
        limit: rowsPerPage,
      });
      if (response.status === "success") {
        setData(response.data.rows || []);
        setTotal(response.data.total_count || 0);
      }
    } catch (error) {
      console.error("Error fetching customer reports:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tabValue, days, page]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(1);
  };

  return (
    <Box sx={{ p: 0 }}>
      {/* Header */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Customer Reports
      </Typography>

      {/* Tabs */}
      <Paper sx={{ mb: 0 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Top Frequent Customers" />
          <Tab label="Inactive Customers" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Frequent Customers */}
          <TabPanel value={tabValue} index={0}>
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Customer Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Mobile</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Total Bookings</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Last Booking Date
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                        <Typography color="text.secondary">
                          No frequent customers found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((row) => (
                      <TableRow key={row.user_id} hover>
                        <TableCell>{row.customer_name}</TableCell>
                        <TableCell>{row.email || "-"}</TableCell>
                        <TableCell>{row.mobile || "-"}</TableCell>
                        <TableCell>{row.total_bookings || 0}</TableCell>
                        <TableCell>
                          {row.last_booking_date
                            ? dayjs(row.last_booking_date).format(
                                "DD/MM/YYYY"
                              )
                            : "Never"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Inactive Customers */}
          <TabPanel value={tabValue} index={1}>
            {/* Day Filter */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 2,
              }}
            >
              <FormControl size="small" sx={{ width: 200 }}>
                <InputLabel>Inactive Since</InputLabel>
                <Select
                  value={days}
                  label="Inactive Since"
                  onChange={(e) => setDays(Number(e.target.value))}
                >
                  <MenuItem value={30}>Last 30 Days</MenuItem>
                  <MenuItem value={60}>Last 60 Days</MenuItem>
                  <MenuItem value={90}>Last 90 Days</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Customer Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Mobile</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Total Bookings</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Registration Date
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                        <Typography color="text.secondary">
                          No inactive customers found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((row) => (
                      <TableRow key={row.user_id} hover>
                        <TableCell>{row.customer_name}</TableCell>
                        <TableCell>{row.email || "-"}</TableCell>
                        <TableCell>{row.mobile || "-"}</TableCell>
                        <TableCell>{row.total_bookings || 0}</TableCell>
                        <TableCell>
                          {dayjs(row.registration_date).format(
                                "DD/MM/YYYY"
                              )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>


            <TablePagination
              component="div"
              count={total}
              page={page - 1}
              onPageChange={(event, newPage) => setPage(newPage + 1)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(1);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
            
          </TabPanel>
        </>
      )}
    </Box>
  );
};

export default CustomerReports;
